import { Close } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import { Text } from '@vestwell-frontend/ui';

import { startOfYear, subMonths } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { useToggle } from 'react-use';

import { CustomRangeForm } from '../CustomRangeForm.component';
import { toISODate, toLocalDate } from './utils';

type CustomRangeFilterProps = {
  formValues: {
    endDate?: string;
    startDate?: string;
  };
  setFormValues: (formValues: { endDate?: string; startDate?: string }) => void;
};

const OPTIONS = [
  {
    content: 'Last 12 Months',
    id: 'last12Months'
  },
  {
    content: 'Last 6 Months',
    id: 'last6Months'
  },
  {
    content: 'Last 3 Months',
    id: 'last3Months'
  },
  {
    content: 'Year to Date',
    id: 'yearToDate'
  },
  {
    content: 'All Time',
    id: 'allTime'
  },
  {
    content: 'Custom Range',
    id: 'customRange'
  }
];

export const CustomRangeFilter: FC<CustomRangeFilterProps> = props => {
  const [dateRange, setDateRange] = useState('last12Months');
  const [isOpen, toggleIsOpen] = useToggle(false);
  const change = useCallback(
    (event: SelectChangeEvent) => {
      const selectedOption = OPTIONS.find(
        option => option.id === event.target.value
      );
      switch (selectedOption.id) {
        case 'last12Months':
          props.setFormValues({
            endDate: toISODate(new Date()),
            startDate: toISODate(subMonths(new Date(), 12))
          });
          setDateRange(selectedOption.id);
          break;
        case 'last6Months':
          props.setFormValues({
            endDate: toISODate(new Date()),
            startDate: toISODate(subMonths(new Date(), 6))
          });
          setDateRange(selectedOption.id);
          break;
        case 'last3Months':
          props.setFormValues({
            endDate: toISODate(new Date()),
            startDate: toISODate(subMonths(new Date(), 3))
          });
          setDateRange(selectedOption.id);
          break;
        case 'yearToDate':
          props.setFormValues({
            endDate: toISODate(new Date()),
            startDate: toISODate(startOfYear(new Date()))
          });
          setDateRange(selectedOption.id);
          break;
        case 'allTime':
          props.setFormValues({
            endDate: toISODate(new Date())
          });
          setDateRange(selectedOption.id);
          break;
      }
    },
    [props, toggleIsOpen]
  );

  const rangeLabel = useMemo(() => {
    if (
      dateRange === 'customRange' &&
      props.formValues.startDate &&
      props.formValues.endDate
    ) {
      return `${toLocalDate(props.formValues.startDate)} - ${toLocalDate(props.formValues.endDate)}`;
    }
    return OPTIONS.find(option => option.id === dateRange)?.content || '';
  }, [props.formValues, dateRange]);

  const onSubmit = useCallback(
    async values => {
      setDateRange(
        `${toLocalDate(values.startDate)} - ${toLocalDate(values.endDate)}`
      );
      props.setFormValues(values);
      setDateRange('customRange');
      toggleIsOpen();
    },
    [setDateRange, toggleIsOpen]
  );

  return (
    <>
      <FormControl size='small' variant='outlined'>
        <InputLabel id='date-range-label'>Date Range</InputLabel>
        <Select
          data-testid='date-range-filter'
          label='Date Range'
          labelId='date-range-label'
          onChange={change}
          renderValue={() => rangeLabel}
          size='small'
          value={dateRange}>
          {OPTIONS.map((option, index) => (
            <MenuItem
              key={option.id}
              onClick={index === OPTIONS.length - 1 && toggleIsOpen}
              value={option.id}>
              {option.content}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Modal onClose={toggleIsOpen} open={isOpen}>
        <Box
          sx={theme => ({
            backgroundColor: 'white.main',
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[24],
            left: '50%',
            maxHeight: '100vh',
            maxWidth: 600,
            overflowY: 'auto',
            padding: theme.spacing(3),
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '65%'
          })}>
          <Stack direction='row' justifyContent='space-between' mb={3}>
            <Text m={0} variant='h5'>
              Custom Range
            </Text>
            <IconButton aria-label='Close' onClick={toggleIsOpen}>
              <Close />
            </IconButton>
          </Stack>
          <CustomRangeForm
            cancel={toggleIsOpen}
            change={onSubmit}
            period={props.formValues}
            submitTitle='Select'
          />
        </Box>
      </Modal>
    </>
  );
};
